/**
 * Dashboard Page (New Home)
 * Overview dashboard with income/expense summary and navigation to financial balance
 */

import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useUIStore } from '../store/uiStore';
import { useExpenseEntries } from '../hooks/useExpenseEntries';
import { useRevenueEntries } from '../hooks/useRevenueEntries';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { PiggyBankIcon } from '../components/icons/CategoryIcons';
import { type SupportedCurrency, CURRENCY_SYMBOLS } from '../types';

// Format number as Brazilian currency
const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface DashboardPageProps {
  onNavigateToBalance: () => void;
}

export function DashboardPage({ onNavigateToBalance }: DashboardPageProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  // Get user preferences for default currency
  const { preferences } = useUserPreferences();
  const defaultCurrency = (preferences?.defaultCurrency as SupportedCurrency) || 'BRL';

  // Current month for data fetching
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Fetch current month expenses - amounts in BRL converted using currencySnapshots
  const { total: totalExpenses, isLoading: expensesLoading } = useExpenseEntries(
    currentYear,
    currentMonth,
    defaultCurrency
  );

  // Fetch current month income - amounts in BRL converted using currencySnapshots
  const { total: totalIncome, isLoading: incomeLoading } = useRevenueEntries(
    currentYear,
    currentMonth,
    defaultCurrency
  );

  const isLoading = expensesLoading || incomeLoading;

  // Calculate percentages for the donut chart
  const total = totalIncome + totalExpenses;
  const incomePercentage = total > 0 ? (totalIncome / total) * 100 : 50;
  const expensesPercentage = total > 0 ? (totalExpenses / total) * 100 : 50;

  // Prepare data for donut chart
  const chartData = [
    { name: 'income', value: incomePercentage, color: '#22c55e' }, // Green for income
    { name: 'expenses', value: expensesPercentage, color: '#f97316' }, // Orange for expenses
  ];

  // Filter out zero values for better chart display
  const filteredChartData = chartData.filter(d => d.value > 0);
  const displayChartData = filteredChartData.length > 0 ? filteredChartData : [
    { name: 'empty', value: 100, color: isDark ? '#334155' : '#e2e8f0' }
  ];

  if (isLoading) {
    return (
      <div className="px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <div className={`animate-pulse ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Donut Chart with Avatar */}
      <div className="relative">
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={displayChartData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={110}
              paddingAngle={2}
              dataKey="value"
              stroke="transparent"
              startAngle={90}
              endAngle={-270}
            >
              {displayChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center Icon - Piggy Bank with coin flip animation on load */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`w-28 h-28 rounded-full flex items-center justify-center animate-coin-flip-once ${
            isDark ? 'bg-slate-700' : 'bg-slate-200'
          }`}>
            <PiggyBankIcon className="w-16 h-16 text-[#F7931A]" />
          </div>
        </div>

        {/* Percentage labels */}
        {total > 0 && (
          <>
            <div className="absolute top-8 right-12 text-sm font-semibold text-green-500">
              {incomePercentage.toFixed(0)}%
            </div>
            <div className="absolute bottom-8 left-12 text-sm font-semibold text-orange-500">
              {expensesPercentage.toFixed(0)}%
            </div>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Income Card (Green) */}
        <div
          className="rounded-xl p-4 bg-gradient-to-br from-green-600 to-green-700 shadow-lg"
          data-testid="income-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-white/80 text-xs font-medium" data-testid="income-label">
            {t('pages.dashboard.incomeMonth')}
          </p>
          <p className="text-white text-xl font-bold mt-1" data-testid="income-value">
            {CURRENCY_SYMBOLS[defaultCurrency]} {formatCurrency(totalIncome)}
          </p>
        </div>

        {/* Expenses Card (Red) */}
        <div
          className="rounded-xl p-4 bg-gradient-to-br from-red-600 to-red-700 shadow-lg"
          data-testid="expenses-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-white/80 text-xs font-medium" data-testid="expenses-label">
            {t('pages.dashboard.expensesMonth')}
          </p>
          <p className="text-white text-xl font-bold mt-1" data-testid="expenses-value">
            {CURRENCY_SYMBOLS[defaultCurrency]} {formatCurrency(totalExpenses)}
          </p>
        </div>
      </div>

      {/* Navigation Button */}
      <button
        onClick={onNavigateToBalance}
        className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold text-lg shadow-lg transition-all flex items-center justify-center gap-3"
        data-testid="balance-button"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        {t('pages.dashboard.balanceButton')}
      </button>
    </div>
  );
}
